/**
 * Iter338 · Dashboard trend chart — 30 days of reclassifications
 * broken down by segment (stacked bars). Fetches
 * /api/leads-finder/quality/trend and renders via Recharts. Rendered
 * inline in the Dashboard after the TaxonomyQualityCard.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend, LineChart, Line,
} from "recharts";
import { TrendingUp, RefreshCw, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Distinct-but-brand-friendly palette for the segment series.
const COLORS = ["#1B3A8A", "#C9A24E", "#7C3AED", "#10B981",
                 "#F59E0B", "#EF4444", "#6B7280"];

export default function ReclassifyTrendCard() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/leads-finder/quality/trend`, { params: { days } });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trend load failed");
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const chartRows = useMemo(() => {
    if (!data) return [];
    return data.series.map((row) => {
      const flat = { date: row.date.slice(5), total: row.total };
      Object.entries(row.by_segment || {}).forEach(([k, v]) => { flat[k] = v; });
      return flat;
    });
  }, [data]);

  const totalRange = data?.grand_total || 0;

  return (
    <section className="al-card p-5" data-testid="reclassify-trend-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#1B3A8A]/10">
              <TrendingUp className="w-4 h-4 text-[#1B3A8A]" />
            </span>
            <h3 className="text-[14px] font-bold">
              {isPt ? "Tendência de reclassificação" : "Reclassification trend"}
            </h3>
          </div>
          <p className="text-[12px] text-[var(--al-text-muted)]">
            {isPt
              ? `${totalRange} reclassificações nos últimos ${days} dias`
              : `${totalRange} reclassifications in the last ${days} days`}
          </p>
        </div>
        <div className="inline-flex items-center gap-1">
          {[7, 30, 60, 90].map((n) => (
            <button key={n} type="button"
              data-testid={`trend-days-${n}`}
              onClick={() => setDays(n)}
              className={`h-7 px-2.5 rounded text-[11px] font-bold border transition ${
                days === n
                  ? "bg-[#1B3A8A] border-[#1B3A8A] text-white"
                  : "border-[var(--al-border)] hover:border-[#1B3A8A] text-[var(--al-text-muted)]"}`}>
              {n}d
            </button>
          ))}
          <button type="button" onClick={load}
            data-testid="trend-refresh"
            title={isPt ? "Recarregar" : "Reload"}
            className="w-7 h-7 rounded border border-[var(--al-border)] hover:border-[#1B3A8A] inline-flex items-center justify-center">
            {loading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="h-56 rounded bg-[var(--al-bg)] animate-pulse" />
      ) : totalRange === 0 ? (
        <div className="h-40 rounded border border-dashed border-[var(--al-border)] flex items-center justify-center text-[12px] text-[var(--al-text-muted)]"
             data-testid="trend-empty">
          {isPt
            ? "Ainda não há reclassificações no período — use o Auto Reclass em Massa para começar."
            : "No reclassifications in this window yet — run Auto Reclass Batch to start."}
        </div>
      ) : (
        <>
          <div className="h-56 w-full" data-testid="trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows}
                margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.08)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} width={30} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8,
                                    background: "var(--al-card-bg, #FFFFFF)",
                                    border: "1px solid var(--al-border)" }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {(data?.segments || []).map((seg, i) => (
                  <Bar key={seg} dataKey={seg} stackId="s"
                       fill={COLORS[i % COLORS.length]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Segment leaderboard */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5"
               data-testid="trend-top-segments">
            {(data?.top_segments || []).map((s, i) => (
              <div key={s.segment}
                   className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border border-[var(--al-border)]">
                <span className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: COLORS[i % COLORS.length] }} />
                <span className="truncate flex-1">{s.segment}</span>
                <span className="font-bold tabular-nums">{s.count}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
